import React, { FC, ReactNode, useId, useRef } from 'react';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import SVGInline from 'components/SVGInline/SVGInline';
import { useBackdropClose } from 'hooks/useBackdropClose/useBackdropClose';
import {
    StyledSVG,
    AudioSubtitleBlock,
    ContentMarker,
    ModalCloseButton,
    ModalNavigation,
    ModalWrapper,
} from 'style/styled-components/reusable.css';
import icons from 'style';
import { usePurchase } from 'providers/usePurchase/PurchaseContext';
import translate from 'utils/fnTranslate';
import { Episode, Marker, Movie, MovieDetails } from 'types/Asset';
import { PictureWithFallback } from 'components/Picture/PictureWithFallback';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import fallbackImageLandscape from 'assets/images/landscape_fallback.png';
import fallbackImagePortrait from 'assets/images/portrait_fallback.png';
import { buildRentalPeriod, secondsToTime } from 'utils/fnDate';
import { withNoScroll } from 'hooks/withNoScroll/withNoScroll';
import { isEpisode } from 'utils/fnTypeGuards';
import { getVodContentMarker } from 'utils/fnContentMarker';
import { getCurrencySymbol, getLanguage, secondsToTimeString } from 'utils/fnData';
import {
    ButtonContainer,
    ImageWrapper,
    ImageWrapperInner,
    InfoWrapper,
    MetaContainer,
    MetaTitle,
    OptionsContent,
    StyledButton,
    TermsAndConditions,
} from './PurchaseOptionConfirmation.css';

type PurchaseConfirmationProps = {
    assetOverride?: Episode | Movie | MovieDetails;
    cancelOverride?: () => void;
    selectPurchaseOfferOverride?: (offer: any) => void;
};

const PurchaseConfirmation: FC<PurchaseConfirmationProps> = ({ assetOverride, cancelOverride, selectPurchaseOfferOverride }) => {
    const { cancel: purchaseCancel, selectPurchaseOffer: purchaseSelectOffer, asset: purchaseAsset, purchasing, isBuyFlow } = usePurchase();

    const asset = assetOverride ?? purchaseAsset;
    const cancel = cancelOverride ?? purchaseCancel;
    const selectPurchaseOffer = selectPurchaseOfferOverride ?? purchaseSelectOffer;
    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const titleId = useId();

    const onClose = () => {
        cancel();
    };

    const backdropClose = useBackdropClose(onClose);

    const onOfferSelected = (term: any) => {
        selectPurchaseOffer(term);
    };

    const getRentDuration = (): ReactNode => {
        if (isBuyFlow) {
            return null;
        }

        const tvodTerm = asset?.vod_terms?.find((term) => term.type === 'TVOD');
        const rentDurationInSeconds = tvodTerm?.watch_period ?? -1;
        const rentDurationInMinutes = rentDurationInSeconds !== -1 ? rentDurationInSeconds / 60 : -1;

        if (rentDurationInMinutes === -1) return null;

        const translatedTemplate = translate('DETAIL_RENTAL_TIME_TEMPLATE', {
            RENTAL_DURATION: buildRentalPeriod(rentDurationInMinutes),
        });

        // Find the last word and wrap it with the number in bold span
        const wrappedTemplate = translatedTemplate.replace(/(\S+)\s+(\S+)$/, '<span class="rent-duration">$1 $2</span>');

        return (
            <InfoWrapper key={'rent-for'}>
                <span
                    aria-hidden
                    dangerouslySetInnerHTML={{
                        __html: `${wrappedTemplate}.`,
                    }}
                />
                <span className="sr-only">
                    {translate('DETAIL_RENTAL_TIME_TEMPLATE', {
                        RENTAL_DURATION: secondsToTimeString(rentDurationInMinutes * 60),
                    })}
                </span>
            </InfoWrapper>
        );
    };

    const getButtonText = (price: number, currency: string, isEpisodeAsset, isBuyOffer = false): string => {
        const currencySymbol = getCurrencySymbol(currency);

        if (isBuyOffer) {
            return translate(isEpisodeAsset ? 'BUY_EPISODE_FOR_PRICE_BUTTON' : 'BUY_FOR_PRICE_BUTTON', {
                price: `${price} ${currencySymbol}`,
            });
        }

        if (isEpisodeAsset) {
            if (price === 0) {
                return translate('RENT_EPISODE_FOR_FREE_BUTTON');
            }

            return translate('RENT_EPISODE_FOR_PRICE_BUTTON', {
                price: `${price} ${currencySymbol}`,
            });
        }
        if (price === 0) {
            return translate('RENT_FOR_FREE_BUTTON');
        }

        // resolution not provided yet
        /*

        switch (term.resolution) {
            case ResolutionType.SD:
                buttonKey = 'RENT_SD_FOR_PRICE_BUTTON';
                break;
            case ResolutionType.HD:
                buttonKey = 'RENT_HD_FOR_PRICE_BUTTON';
                break;
            case ResolutionType['4K']:
                buttonKey = 'RENT_4K_FOR_PRICE_BUTTON';
                break;
            default:
                buttonKey = 'RENT_FOR_PRICE_BUTTON';
                break;
        }
        */

        return translate('RENT_FOR_PRICE_BUTTON', { price_euros: `${price} ${currencySymbol}` });
    };

    const getButtons = (): ReactNode => {
        const terms = asset.vod_terms?.filter((term) => term.type === 'TVOD') ?? [];

        if (terms?.length) {
            return terms
                .sort((a, b) => b.price - a.price)
                .map((term, index) => {
                    const clickListener = () => onOfferSelected(term);
                    const buttonText = getButtonText(term.price, term.currency, isEpisode(asset));

                    return (
                        <StyledButton
                            key={`rent-${term.id}-${index}`}
                            label={buttonText}
                            onClick={clickListener}
                            isPrimary={index === 0}
                            data-event-name={'click'}
                            data-track-id={'button_rent'}
                            data-button-title={buttonText}
                            data-page-name={'RentConfirmation'}
                        >
                            <StyledSVG svg={icons.cartIcon} />
                        </StyledButton>
                    );
                });
        }

        const offers = (isBuyFlow ? asset.buyOffers : asset.rentOffers) ?? [];

        if (offers.length) {
            return [...offers]
                .sort((a, b) => b.offerValue - a.offerValue)
                .map((offer, index) => {
                    const buttonText = getButtonText(offer.offerValue, offer.currency, isEpisode(asset), isBuyFlow);

                    return (
                        <StyledButton
                            key={`offer-${offer.id ?? offer.offerId}-${index}`}
                            label={buttonText}
                            onClick={() => onOfferSelected(offer)}
                            isPrimary={index === 0}
                            data-event-name={'click'}
                            data-track-id={isBuyFlow ? 'button_buy' : 'button_rent'}
                            data-button-title={buttonText}
                            data-page-name={'RentConfirmation'}
                        >
                            <StyledSVG svg={icons.cartIcon} />
                        </StyledButton>
                    );
                });
        }

        return null;
    };

    const getAudios = (): ReactNode => {
        const audios = asset.audio_languages;

        if (!audios || audios.length === 0) return null;

        return (
            <AudioSubtitleBlock>
                {`${translate('DETAIL_AUDIO_LANGUAGE')} `}
                {audios.map((audio) => getLanguage(audio, translate(`LANGUAGE_${audio.toUpperCase()}`))).join(', ')}
            </AudioSubtitleBlock>
        );
    };

    const getSubtitles = (): ReactNode => {
        const subtitles = asset.subtitle_languages;

        if (!subtitles || subtitles.length === 0) {
            return (
                <AudioSubtitleBlock>
                    {`${translate('DETAIL_SUBTITLES')} `}
                    {translate('SUBTITLES_NONE')}
                </AudioSubtitleBlock>
            );
        }

        return (
            <AudioSubtitleBlock>
                {`${translate('DETAIL_SUBTITLES')} `}
                {subtitles.map((subtitle) => getLanguage(subtitle, translate(`LANGUAGE_${subtitle.toUpperCase()}`))).join(', ')}
            </AudioSubtitleBlock>
        );
    };

    const getContentMarker = (): ReactNode => {
        // firstAvailability is not provided yet
        const { price } = asset;
        const contentMarker: Marker = getVodContentMarker(price);

        return contentMarker && <ContentMarker marker={contentMarker}>{contentMarker.value}</ContentMarker>;
    };

    return (
        <ModalDialog
            as={ModalWrapper}
            onClose={onClose}
            initialFocusRef={buttonContainerRef}
            data-test-id={'header-rentals-rent-modal'}
            opacity={1}
            aria-labelledby={titleId}
            {...backdropClose}
        >
            <ModalNavigation {...backdropClose}>
                <ModalCloseButton
                    type="button"
                    data-test-id={'header-rentals-modal-close-button'}
                    onClick={onClose}
                    tabIndex={0}
                    aria-label={translate('CLOSE_BUTTON')}
                >
                    <SVGInline svg={icons.closeIcon} />
                </ModalCloseButton>
            </ModalNavigation>
            <OptionsContent>
                <ImageWrapper>
                    <ImageWrapperInner>
                        <PictureWithFallback
                            src={asset.image}
                            fit={'cover'}
                            fallbackImage={isEpisode(asset) ? fallbackImageLandscape : fallbackImagePortrait}
                        />
                    </ImageWrapperInner>
                </ImageWrapper>
                <MetaContainer>
                    <MetaTitle id={titleId} data-test-id={'header-rentals-modal-title'}>{`${translate(
                        isBuyFlow ? 'SCREEN_PURCHASE_CONFIRMATION_HEADER' : 'SCREEN_RENT_CONFIRMATION_HEADER'
                    )} "${asset.title}"`}</MetaTitle>
                    <InfoWrapper key={'duration'}>
                        <span aria-hidden>{`${secondsToTime(asset.duration)}.`}</span>
                        <span className="sr-only">{secondsToTimeString(asset.duration)}</span>
                    </InfoWrapper>
                    {getContentMarker()}
                    {getAudios()}
                    {getSubtitles()}
                    <TermsAndConditions>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: translate(isBuyFlow ? 'SCREEN_PURCHASE_CONFIRMATION_TERMS' : 'SCREEN_RENT_CONFIRMATION_TERMS', {
                                    title: `"${asset.title}"`,
                                }).replace(/\n/g, '<div class="spacer"></div>'),
                            }}
                        />
                    </TermsAndConditions>
                    {getRentDuration()}
                    <ButtonContainer ref={buttonContainerRef}>{getButtons()}</ButtonContainer>
                </MetaContainer>
            </OptionsContent>
            <LoadingIndicator overlay={true} isLoading={purchasing} />
        </ModalDialog>
    );
};

export const PurchaseOptionConfirmation = withNoScroll(PurchaseConfirmation);
